import React, { useEffect, useRef } from 'react';
import { VideoRTC } from '../vendor/video-rtc';

const ELEMENT = 'eye-preview-rtc';

if (typeof window !== 'undefined' && !customElements.get(ELEMENT)) {
  class EyePreviewRTC extends VideoRTC {
    constructor() {
      super();
      this._eyeMuted = true;
    }

    set eyeMuted(value) {
      this._eyeMuted = !!value;
      this.applyEyeMuted();
    }

    get eyeMuted() {
      return this._eyeMuted;
    }

    applyEyeMuted() {
      if (!this.video) return;
      this.video.muted = this._eyeMuted;
      this.video.volume = this._eyeMuted ? 0 : 1;
    }

    oninit() {
      super.oninit();
      if (this.video) {
        this.video.controls = false;
        this.video.playsInline = true;
        this.video.style.objectFit = 'contain';
        this.video.style.background = '#111';
        this.applyEyeMuted();
      }
    }

    onconnect() {
      if (!this.isConnected || !this.wsURL || this.ws || this.pc) return false;
      this.wsState = WebSocket.CONNECTING;
      this.connectTS = Date.now();
      this.ws = new WebSocket(this.wsURL);
      this.ws.binaryType = 'arraybuffer';
      this.ws.addEventListener('open', () => this.onopen());
      this.ws.addEventListener('close', (ev) => {
        const detail = { code: ev.code, reason: ev.reason || '' };
        this._eyeLastClose = detail;
        this.dispatchEvent(new CustomEvent('eye-ws-close', { detail }));
        this.onclose();
      });
      return true;
    }

    onclose() {
      const last = this._eyeLastClose || {};
      if (last.code === 1011 || last.code === 4401 || last.code === 4404) {
        this.wsState = WebSocket.CLOSED;
        this.ws = null;
        return false;
      }
      return super.onclose();
    }

    /** Respect user unmute — only fall back to muted autoplay when sound is off. */
    play() {
      if (!this.video) return;
      this.applyEyeMuted();
      this.video.play().catch(() => {
        if (this._eyeMuted) return;
        this.video.muted = true;
        this.video.volume = 0;
        this.video.play().catch(() => {});
      });
    }
  }
  customElements.define(ELEMENT, EyePreviewRTC);
}

function applyMutedToHost(host, muted) {
  if (!host) return null;
  host.eyeMuted = muted;
  const video = host.video || host.querySelector?.('video');
  if (video) {
    video.muted = muted;
    video.volume = muted ? 0 : 1;
  }
  return video;
}

export default function ConfiguredMsePlayer({
  src,
  hidden = false,
  muted = true,
  onPlaying,
  onError,
}) {
  const hostRef = useRef(null);
  const mutedRef = useRef(muted);
  const onPlayingRef = useRef(onPlaying);
  const onErrorRef = useRef(onError);
  onPlayingRef.current = onPlaying;
  onErrorRef.current = onError;
  mutedRef.current = muted;

  // Connect/disconnect WebSocket only when src changes — never on mute toggle.
  useEffect(() => {
    const el = hostRef.current;
    if (!el || !src) return undefined;

    el.mode = 'mse,webrtc';
    el.media = 'video,audio';
    el.background = true;
    el.visibilityCheck = false;
    el.eyeMuted = mutedRef.current;
    el.src = src;

    const handlePlaying = () => {
      applyMutedToHost(el, mutedRef.current);
      if (onPlayingRef.current) onPlayingRef.current();
    };
    const handleError = () => {
      if (onErrorRef.current) onErrorRef.current({});
    };
    const handleWsClose = (ev) => {
      const detail = ev?.detail || {};
      const code = Number(detail.code || 0);
      if (code === 1000 || code === 1001 || code === 1005) return;
      if (onErrorRef.current) onErrorRef.current(detail);
    };

    el.addEventListener('eye-ws-close', handleWsClose);

    const video = applyMutedToHost(el, mutedRef.current);
    if (video) {
      video.addEventListener('playing', handlePlaying);
      video.addEventListener('error', handleError);
    }

    let pollId = null;
    if (!video) {
      let attempts = 0;
      pollId = window.setInterval(() => {
        attempts += 1;
        const v = applyMutedToHost(el, mutedRef.current);
        if (v || attempts > 40) {
          if (pollId) window.clearInterval(pollId);
          if (v) {
            v.addEventListener('playing', handlePlaying);
            v.addEventListener('error', handleError);
          }
        }
      }, 100);
    }

    return () => {
      if (pollId) window.clearInterval(pollId);
      el.removeEventListener('eye-ws-close', handleWsClose);
      const v = el.video || el.querySelector?.('video');
      if (v) {
        v.removeEventListener('playing', handlePlaying);
        v.removeEventListener('error', handleError);
      }
      if (typeof el.ondisconnect === 'function') {
        el.ondisconnect();
      }
    };
  }, [src]);

  // UI-only mute — does not reconnect go2rtc / configured-preview-mse.
  useEffect(() => {
    const el = hostRef.current;
    if (!el) return undefined;
    const video = applyMutedToHost(el, muted);
    if (!video) return undefined;
    if (!muted && video.paused) {
      video.play().catch(() => {});
    }
    return undefined;
  }, [muted]);

  const style = hidden
    ? {
        position: 'absolute',
        width: 1,
        height: 1,
        opacity: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
      }
    : {
        display: 'block',
        width: '100%',
        height: '100%',
      };

  return React.createElement(ELEMENT, { ref: hostRef, style });
}
